import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Toaster, toast } from "sonner";
import { site } from "../content/site";
import PageHero from "../components/PageHero";

const initial = {
  name: "",
  email: "",
  company: "",
  message: "",
};

// Web3Forms endpoint — submissions are emailed to the address registered
// with the access key on web3forms.com.
//
// Web3Forms access keys are tied to the domain they were created for, so we
// pick the right key based on which domain the visitor is on. Both keys send
// to hello@hawkedge.ai.
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const WEB3FORMS_KEYS = {
  // hawkedgeadvisory.com (and www subdomain)
  "hawkedgeadvisory.com": "990d01eb-6674-44e5-8202-5d997fe15775",
  "www.hawkedgeadvisory.com": "990d01eb-6674-44e5-8202-5d997fe15775",
  // hawkedge.ai (and www subdomain) — primary
  "hawkedge.ai": "0b758e5a-bd99-42ae-93e9-64da3d6db381",
  "www.hawkedge.ai": "0b758e5a-bd99-42ae-93e9-64da3d6db381",
};
const WEB3FORMS_DEFAULT_KEY = "0b758e5a-bd99-42ae-93e9-64da3d6db381";

function getAccessKey() {
  if (typeof window === "undefined") return WEB3FORMS_DEFAULT_KEY;
  const host = window.location.hostname.toLowerCase();
  return WEB3FORMS_KEYS[host] || WEB3FORMS_DEFAULT_KEY;
}

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: getAccessKey(),
          subject: `New inquiry — ${form.name.trim()}${form.company.trim() ? ` (${form.company.trim()})` : ""}`,
          from_name: "HawkEdge AI Advisory — Contact Form",
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || "(not provided)",
          message: form.message.trim(),
          replyto: form.email.trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }

      setSubmitted(true);
      setForm(initial);
      toast.success(site.contact.form.success);
    } catch (err) {
      setError(site.contact.form.error);
      toast.error(site.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#5C0E45",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#FDF6F5",
            borderRadius: 0,
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
      <PageHero
        eyebrow={site.contact.eyebrow}
        title={site.contact.title}
        intro={site.contact.intro}
      />

      <section
        className="py-20 sm:py-28 border-b border-white/10"
        data-testid="contact-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <aside className="lg:col-span-4">
            <div className="border-t border-white/10 pt-8">
              <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/50 mb-2">
                Direct
              </p>
              <a
                href={`mailto:${site.brand.email}`}
                data-testid="contact-email-link"
                className="font-serif-display text-2xl sm:text-3xl text-white hover:text-white/80 transition-colors break-words"
              >
                {site.brand.email}
              </a>
            </div>
            <div className="mt-10 border-t border-white/10 pt-8">
              <p className="text-[10px] font-sans-modern uppercase tracking-[0.3em] text-white/50 mb-2">
                Location
              </p>
              <p
                data-testid="contact-location"
                className="font-sans-modern text-base text-white/85 leading-relaxed"
              >
                Lakewood, NJ 08701
              </p>
              <div className="mt-5 border border-white/15 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps?q=Lakewood+Township%2C+NJ&ll=40.0968%2C-74.2179&z=11&output=embed"
                  className="w-full aspect-[4/3] block"
                  style={{ filter: "grayscale(0.15) contrast(1.05)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Map of Lakewood, NJ geographic area"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-white/15 p-10 sm:p-14 bg-[#5C0E45]"
              >
                <div className="w-12 h-12 border border-white/40 flex items-center justify-center mb-6">
                  <Check size={20} className="text-white" />
                </div>
                <h3 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight">
                  {site.contact.form.success}
                </h3>
                <button
                  type="button"
                  data-testid="contact-reset"
                  onClick={() => setSubmitted(false)}
                  className="mt-8 inline-flex items-center text-sm font-sans-modern font-semibold text-white border-b border-white/40 pb-1 hover:border-white"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              >
                <Field label="Name" required>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-input-name"
                    className="contact-input"
                    autoComplete="name"
                  />
                </Field>

                <Field label="Email" required>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-input-email"
                    className="contact-input"
                    autoComplete="email"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Company">
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={onChange}
                      data-testid="contact-input-company"
                      className="contact-input"
                      autoComplete="organization"
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="Message" required>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      data-testid="contact-input-message"
                      rows={6}
                      className="contact-input min-h-[150px] py-3 resize-y"
                    />
                  </Field>
                </div>

                {error && (
                  <p
                    data-testid="contact-error"
                    className="sm:col-span-2 text-sm font-sans-modern text-red-200"
                  >
                    {error}
                  </p>
                )}

                <div className="sm:col-span-2 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs font-sans-modern text-white/50 max-w-md">
                    By submitting, you consent to HawkEdge AI Advisory contacting
                    you regarding your inquiry.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group inline-flex items-center justify-center h-12 px-10 bg-white text-[#711457] font-sans-modern font-semibold text-sm tracking-wide hover:bg-white/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending..." : site.contact.form.submit}
                    {!loading && (
                      <ArrowRight
                        size={16}
                        className="ml-2 group-hover:translate-x-1 transition-transform"
                      />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .contact-input {
          width: 100%;
          height: 48px;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.22);
          color: #FDF6F5;
          padding: 0 14px;
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          letter-spacing: 0.01em;
          outline: none;
          transition: border-color 200ms ease;
        }
        .contact-input:focus {
          border-color: #FDF6F5;
        }
        .contact-input::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
    </>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[10px] font-sans-modern uppercase tracking-[0.25em] text-white/50 mb-2">
      {label}
      {required && <span className="text-white/80"> *</span>}
    </span>
    {children}
  </label>
);

export default Contact;
